import type { SpatialWindow } from '@/types';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useWindowStore } from '@/store/windowStore';
import { useEngine } from '@/app/EngineContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { restoreWindow, revealWindow } from '@/features/windows/windowManager';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import styles from './Dock.module.css';

/**
 * Running windows, minimized ones included. Clicking reveals (restoring if needed).
 * Subscribes to a compact string of window ids/states so unrelated edits never
 * re-render it.
 */
export const Dock = () => {
  const { applications, scene } = useEngine();
  const breakpoint = useBreakpoint();
  const key = useWorkspaceStore((s) => {
    const doc = s.workspaces[s.activeWorkspaceId];
    if (!doc) return '';
    return Object.values(doc.objects)
      .filter((o): o is SpatialWindow => o.kind === 'window')
      .sort((a, b) => a.metadata.createdAt - b.metadata.createdAt)
      .map((w) => `${w.id}|${w.name}|${w.applicationId}|${w.windowState}`)
      .join('\n');
  });
  const activeId = useWindowStore((s) => s.activeWindowId);
  if (!key || breakpoint === 'compact') return null;
  const entries = key.split('\n').map((line) => {
    const [id = '', name = '', applicationId = '', windowState = ''] = line.split('|');
    return { id, name, applicationId, windowState };
  });
  return (
    <Paper
      component="nav"
      className={styles.dock}
      aria-label="Windows"
      data-testid="dock"
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
    >
      {entries.map((w) => {
        const id = w.id as SpatialWindow['id'];
        const app = applications.get(w.applicationId as SpatialWindow['applicationId']);
        return (
          <Button
            key={w.id}
            className={styles.item}
            color="inherit"
            variant={activeId === id ? 'outlined' : 'text'}
            aria-pressed={activeId === id}
            data-active={activeId === id || undefined}
            data-minimized={w.windowState === 'minimized' || undefined}
            data-testid="dock-item"
            title={w.name}
            onClick={() => {
              if (w.windowState === 'minimized') restoreWindow(id);
              else revealWindow(scene, id);
            }}
          >
            <span aria-hidden="true">{app?.icon ?? '▢'}</span>
            <span className={styles.label}>{w.name}</span>
          </Button>
        );
      })}
    </Paper>
  );
};
